import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductDetailsModal, Query } from "../../interfaces/ProductObject";
import {
  loadProductDetailsById,
  loadPublicProductDetailsById,
  loadProductDetailsByIds,
  loadProductDetailsByUserId,
  postAskQuery,
  postAnswerQuery,
} from "../../services/thunks/ProductDetailsThunks";
import { resetAllState } from "./resetAllState";

type ProductDetailsState = {
  data: Record<string, ProductDetailsModal>;
  loading: boolean;
  error: any;
  activeProductId: string | null;
};

const initialState: ProductDetailsState = {
  data: {},
  loading: false,
  error: null,
  activeProductId: null,
};

const productDetailsSlice = createSlice({
  name: "ProductDetailsDb",
  initialState,
  reducers: {
    setActiveProductId(state, action: PayloadAction<string | null>) {
      state.activeProductId = action.payload;
    },
    insertProductDetails(
      state,
      action: PayloadAction<{
        productId: string;
        productDetail: ProductDetailsModal;
      }>,
    ) {
      const { productId, productDetail } = action.payload;
      state.data[productId] = productDetail;
    },
    removeProductDetails(state, action: PayloadAction<string>) {
      delete state.data[action.payload];
    },
    clearProductDetails(state) {
      state.data = {};
      state.loading = false;
      state.error = null;
      state.activeProductId = null;
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const onPending = (state: ProductDetailsState) => {
      state.loading = true;
      state.error = null;
    };
    const onRejected = (state: ProductDetailsState, action: any) => {
      state.loading = false;
      state.error = action.error?.message || "An error occurred";
    };
    builder
      .addCase(loadProductDetailsById.pending, onPending)
      .addCase(loadPublicProductDetailsById.pending, onPending)
      .addCase(loadProductDetailsByIds.pending, onPending)
      .addCase(loadProductDetailsByUserId.pending, onPending)
      .addCase(loadProductDetailsById.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          ...state.data,
          ...(action.payload as unknown as Record<string, ProductDetailsModal>),
        };
      })
      .addCase(loadPublicProductDetailsById.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          ...state.data,
          ...(action.payload as unknown as Record<string, ProductDetailsModal>),
        };
      })
      .addCase(loadProductDetailsByIds.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          ...state.data,
          ...(action.payload as unknown as Record<string, ProductDetailsModal>),
        };
      })
      .addCase(loadProductDetailsByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          ...state.data,
          ...(action.payload as unknown as Record<string, ProductDetailsModal>),
        };
      })
      .addCase(postAskQuery.pending, onPending)
      .addCase(postAskQuery.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, query } = action.payload as {
          productId: string;
          query?: Query;
        };
        const existing = state.data[productId];
        // Only append if we have a valid query object; otherwise rely on the refresh
        if (existing && query && (query.question || query.answer || query.id)) {
          state.data[productId] = {
            ...existing,
            queries: [...(existing.queries ?? []), query],
          };
        }
      })
      .addCase(postAnswerQuery.pending, onPending)
      .addCase(postAnswerQuery.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadProductDetailsById.rejected, onRejected)
      .addCase(loadPublicProductDetailsById.rejected, onRejected)
      .addCase(loadProductDetailsByIds.rejected, onRejected)
      .addCase(loadProductDetailsByUserId.rejected, onRejected)
      .addCase(postAskQuery.rejected, onRejected)
      .addCase(postAnswerQuery.rejected, onRejected)
      .addCase(resetAllState, () => initialState);
  },
});

export const {
  setActiveProductId,
  insertProductDetails,
  removeProductDetails,
  clearProductDetails,
  resetError,
} = productDetailsSlice.actions;
export default productDetailsSlice.reducer;

export const selectProductDetailsState = (state: any) => state.productDetails;
export const selectProductDetailsById = (state: any, productId: string) =>
  state.productDetails.data[productId];
